import React, { useState, useEffect, useRef } from 'react';
import {
  Settings, Cpu, Key, Save, RefreshCw, AlertCircle, CheckCircle,
  ShieldX, Zap, Info, Link, Trash2, Eye, EyeOff,
  Bot, Puzzle, SlidersHorizontal, Building2, Mail
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import PageInfoButton from '@/components/PageInfoButton';

type SettingsSection = 'ai' | 'integrations' | 'email' | 'platform';

interface AISettings {
  ai_provider: string;
  ai_api_key: string;
  ai_model: string;
}

interface ModelInfo {
  id: string;
  label: string;
  contextK: number;  // context window in thousands of tokens
}

// System Settings access is controlled by the system.config permission
// No hardcoded email restriction - any system admin can manage settings

const PROVIDER_MODELS: Record<string, ModelInfo[]> = {
  openai: [
    { id: 'gpt-4.1',      label: 'GPT-4.1 (Latest · 1M ctx)',       contextK: 1047 },
    { id: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (Fast · 1M ctx)',    contextK: 1047 },
    { id: 'gpt-4.1-nano', label: 'GPT-4.1 Nano (Fastest · 1M ctx)', contextK: 1047 },
    { id: 'gpt-4o',       label: 'GPT-4o (128k ctx)',                contextK: 128  },
    { id: 'gpt-4o-mini',  label: 'GPT-4o Mini (128k ctx)',           contextK: 128  },
    { id: 'o3-mini',      label: 'O3 Mini (200k ctx)',                contextK: 200  },
    { id: 'gpt-4-turbo',  label: 'GPT-4 Turbo (128k ctx)',           contextK: 128  },
    { id: 'gpt-4',        label: 'GPT-4 (8k ctx — legacy)',           contextK: 8    }
  ],
  claude: [
    { id: 'claude-opus-4-6',            label: 'Claude Opus 4.6 (200k ctx)',    contextK: 200 },
    { id: 'claude-sonnet-4-6',          label: 'Claude Sonnet 4.6 (200k ctx)',  contextK: 200 },
    { id: 'claude-3-7-sonnet-20250219', label: 'Claude 3.7 Sonnet (200k ctx)', contextK: 200 },
    { id: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet (200k ctx)', contextK: 200 },
    { id: 'claude-3-5-haiku-20241022',  label: 'Claude 3.5 Haiku (200k ctx)',  contextK: 200 },
    { id: 'claude-3-5-sonnet-20240620', label: 'Claude 3.5 Sonnet v1 (200k)', contextK: 200  },
    { id: 'claude-3-opus-20240229',     label: 'Claude 3 Opus (200k ctx)',     contextK: 200  },
    { id: 'claude-3-haiku-20240307',    label: 'Claude 3 Haiku (200k ctx)',    contextK: 200  }
  ],
  gemini: [
    { id: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro (1M ctx)', contextK: 1000 },
    { id: 'gemini-2.0-flash',             label: 'Gemini 2.0 Flash (1M ctx)', contextK: 1000 },
    { id: 'gemini-1.5-pro',               label: 'Gemini 1.5 Pro (2M ctx)', contextK: 2000 },
    { id: 'gemini-1.5-flash',             label: 'Gemini 1.5 Flash (1M ctx)', contextK: 1000 },
    { id: 'gemini-pro',                   label: 'Gemini Pro (32k ctx)', contextK: 32 },
    { id: 'gemini-pro-vision',            label: 'Gemini Pro Vision (16k ctx)', contextK: 16 }
  ],
  zai: [
    { id: 'glm-4-plus',  label: 'GLM-4 Plus (128k ctx)',        contextK: 128 },
    { id: 'glm-4-long',  label: 'GLM-4 Long (1M ctx)',          contextK: 1000 },
    { id: 'glm-4-air',   label: 'GLM-4 Air (128k ctx)',         contextK: 128 },
    { id: 'glm-4-airx',  label: 'GLM-4 Air-X (128k ctx)',       contextK: 128 },
    { id: 'glm-4-flash', label: 'GLM-4 Flash (128k ctx · Fast)', contextK: 128 },
    { id: 'glm-4v-plus', label: 'GLM-4V Plus (Vision)',         contextK: 32  },
    { id: 'glm-4v',      label: 'GLM-4V (Vision)',              contextK: 8   }
  ],
  xai: [
    { id: 'grok-4-latest',        label: 'Grok 4 Latest (128k ctx)',    contextK: 128 },
    { id: 'grok-code-fast-1',     label: 'Grok Code Fast (128k ctx)',   contextK: 128 },
    { id: 'grok-2-1212',          label: 'Grok 2.0 (131k ctx)',         contextK: 131 },
    { id: 'grok-2-vision-1212',   label: 'Grok 2.0 Vision (8k ctx)',    contextK: 8   },
    { id: 'grok-2',               label: 'Grok 2.0 (131k ctx)',         contextK: 131 },
    { id: 'grok-beta',            label: 'Grok Beta (131k ctx)',         contextK: 131 }
  ]
};

const DEFAULT_MODELS: Record<string, string> = {
  openai: 'gpt-4o',
  claude: 'claude-sonnet-4-6',
  gemini: 'gemini-2.0-flash',
  zai:    'glm-4-plus',
  xai:    'grok-4-latest'
};

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  claude: 'Anthropic Claude',
  gemini: 'Google Gemini',
  zai:    'Z.AI (Zhipu)',
  xai:    'xAI Grok'
};

const SystemSettings: React.FC = () => {
  const { hasPermission, user } = useAuth();

  if (!hasPermission('system.config')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md bg-white rounded-3xl border border-slate-100 p-8">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Access Denied</h1><PageInfoButton /></div>
          <p className="text-slate-600 mt-2">Only System Administrators can access AI Settings.</p>
        </div>
      </div>
    );
  }

  return <AISettingsContent />;
};

const AISettingsContent: React.FC = () => {
  const [settings, setSettings] = useState<AISettings>({ ai_provider: 'openai', ai_api_key: '', ai_model: 'gpt-4o' });
  const [isLoading, setIsLoading]   = useState(true);
  const [isSaving, setIsSaving]     = useState(false);
  const [isTesting, setIsTesting]   = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error' | 'testing'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [testMessage, setTestMessage]   = useState('');
  const [useCustomProvider, setUseCustomProvider] = useState(false);
  const [useCustomModel, setUseCustomModel]       = useState(false);
  const [showAiKey, setShowAiKey] = useState(false);
  // Tracks the real saved key length (never the key itself) per provider
  const savedKeyLengthsRef = useRef<Record<string, number>>({});
  const [savedKeyLength, setSavedKeyLength] = useState(0);
  // Whether the user is actively typing a new key (vs seeing the bullet placeholder)
  const [isEditingKey, setIsEditingKey] = useState(false);

  const [activeSection, setActiveSection] = useState<SettingsSection>(() => {
    const saved = localStorage.getItem('systemSettingsTab');
    return (saved && ['platform', 'ai', 'integrations', 'email'].includes(saved)) ? saved as SettingsSection : 'platform';
  });

  const handleTabChange = (tab: SettingsSection) => {
    setActiveSection(tab);
    localStorage.setItem('systemSettingsTab', tab);
  };

  const savedModelsRef  = useRef<Record<string, string>>({});
  const savedApiKeysRef = useRef<Record<string, string>>({});

  // Companies House integration state
  const [chStatus, setChStatus]       = useState<{ configured: boolean; source: string; maskedKey: string | null } | null>(null);
  const [chKey, setChKey]             = useState('');
  const [chShowKey, setChShowKey]     = useState(false);
  const [chSaving, setChSaving]       = useState(false);
  const [chRemoving, setChRemoving]   = useState(false);
  const [chMsg, setChMsg]             = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // SMTP / Email settings state
  const [emailSettings, setEmailSettings] = useState({
    email_host: '',
    email_port: 587,
    email_secure: false,
    email_user: '',
    email_password: '',
    default_from_email: ''
  });
  const [loadingEmail, setLoadingEmail]   = useState(false);
  const [emailSaveMsg, setEmailSaveMsg]   = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadEmailSettings = async () => {
    setLoadingEmail(true);
    try {
      const res = await fetch('/api/settings/system', {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const result = await res.json();
        const parsed: any = {};
        (result.data as any[]).forEach(cfg => {
          if (cfg.category === 'email') {
            let val = cfg.config_value;
            if (cfg.config_type === 'number')  val = parseInt(val);
            else if (cfg.config_type === 'boolean') val = val === '1' || val === 'true';
            parsed[cfg.config_key] = val;
          }
        });
        setEmailSettings(prev => ({ ...prev, ...parsed }));
      }
    } catch (err) {
      console.error('Failed to load email settings', err);
    } finally {
      setLoadingEmail(false);
    }
  };

  const saveEmailSettings = async () => {
    setEmailSaveMsg(null);
    try {
      const csrf = getCsrfToken();
      const configs = Object.entries(emailSettings).map(([key, value]) => ({
        key,
        value: String(value),
        type: typeof value === 'boolean' ? 'boolean' : typeof value === 'number' ? 'number' : 'string'
      }));
      const res = await fetch('/api/settings/system', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        body: JSON.stringify({ configs })
      });
      if (res.ok) {
        setEmailSaveMsg({ type: 'success', text: 'SMTP settings saved successfully.' });
        loadEmailSettings();
      } else {
        throw new Error('Save failed');
      }
    } catch (err) {
      setEmailSaveMsg({ type: 'error', text: 'Failed to save SMTP settings. Please try again.' });
    }
  };

  useEffect(() => { loadSettings(); loadChStatus(); }, []);
  useEffect(() => { if (activeSection === 'email') loadEmailSettings(); }, [activeSection]);

  const getToken = () => localStorage.getItem('token');
  const getCsrfToken = (): string | null => {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  };

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const res = await fetch('/api/admin/settings/ai', {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) return;
      const { data = {} } = await res.json();

      const provider = data.ai_provider || 'openai';
      const model    = data.ai_model    || DEFAULT_MODELS[provider] || '';

      const isPredefinedProvider = Object.keys(PROVIDER_MODELS).includes(provider);
      const isPredefinedModel    = isPredefinedProvider &&
        (PROVIDER_MODELS[provider]?.some((m: ModelInfo) => m.id === model) ?? false);

      setUseCustomProvider(!isPredefinedProvider);
      setUseCustomModel(!isPredefinedModel);
      setSettings({ ai_provider: provider, ai_api_key: '', ai_model: model });

      const keyLen = data.ai_api_key_length || 0;
      setSavedKeyLength(keyLen);
      setIsEditingKey(false);

      // Initialise per-provider memory
      savedModelsRef.current  = Object.fromEntries(Object.keys(PROVIDER_MODELS).map(p => [p, DEFAULT_MODELS[p] || '']));
      savedModelsRef.current[provider] = model;
      savedApiKeysRef.current = Object.fromEntries(Object.keys(PROVIDER_MODELS).map(p => [p, '']));
      savedKeyLengthsRef.current[provider] = keyLen;
    } catch (e) {
      console.error('Failed to load AI settings:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadChStatus = async () => {
    try {
      const res = await fetch('/api/settings/integrations/companies-house', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) return;
      const { data } = await res.json();
      setChStatus(data);
    } catch { /* silent */ }
  };

  const handleChSave = async () => {
    if (!chKey.trim()) return;
    setChSaving(true); setChMsg(null);
    try {
      const csrf = getCsrfToken();
      const res = await fetch('/api/settings/integrations/companies-house', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        body: JSON.stringify({ apiKey: chKey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setChKey('');
      setChMsg({ type: 'success', text: 'Companies House API key saved successfully.' });
      await loadChStatus();
    } catch (e: any) {
      setChMsg({ type: 'error', text: e.message || 'Failed to save key.' });
    } finally { setChSaving(false); }
  };

  const handleChRemove = async () => {
    setChRemoving(true); setChMsg(null);
    try {
      const csrf = getCsrfToken();
      const res = await fetch('/api/settings/integrations/companies-house', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setChMsg({ type: 'success', text: 'API key removed.' });
      await loadChStatus();
    } catch (e: any) {
      setChMsg({ type: 'error', text: e.message || 'Failed to remove key.' });
    } finally { setChRemoving(false); }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus('idle');
      setErrorMessage('');

      // Only send ai_api_key if the user actually typed a new one
      const payload: any = {
        ai_provider: settings.ai_provider,
        ai_model: settings.ai_model,
      };
      if (isEditingKey && settings.ai_api_key) {
        payload.ai_api_key = settings.ai_api_key;
      }

      const csrfToken = getCsrfToken();
      const res = await fetch('/api/admin/settings/ai', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setSaveStatus('success');
        setTestStatus('idle');
        // After save, if a new key was entered reload the length
        if (isEditingKey && settings.ai_api_key) {
          savedKeyLengthsRef.current[settings.ai_provider] = settings.ai_api_key.length;
          setSavedKeyLength(settings.ai_api_key.length);
          setSettings(prev => ({ ...prev, ai_api_key: '' }));
          setIsEditingKey(false);
        }
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (e) {
      setSaveStatus('error');
      setErrorMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    const hasKey = (isEditingKey && settings.ai_api_key) || savedKeyLength > 0;
    if (!hasKey || !settings.ai_provider) {
      setTestStatus('error');
      setTestMessage('Please enter an API key and select a provider first.');
      return;
    }
    try {
      setIsTesting(true);
      setTestStatus('testing');
      setTestMessage('');

      // Save first then test
      await handleSave();

      const csrfToken = getCsrfToken();
      const res = await fetch(`/api/settings/ai/test/${settings.ai_provider}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
        }
      });
      const data = await res.json();
      if (res.ok && data.success !== false) {
        setTestStatus('success');
        setTestMessage('Connection successful! Provider is responding.');
      } else {
        setTestStatus('error');
        setTestMessage(data.message || 'Connection test failed. Check your API key.');
      }
    } catch {
      setTestStatus('error');
      setTestMessage('Connection test failed. Check your network and API key.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleProviderChange = (value: string) => {
    const isCustom = value === 'custom';

    if (!useCustomProvider && PROVIDER_MODELS[settings.ai_provider]) {
      savedModelsRef.current[settings.ai_provider] = settings.ai_model;
      if (isEditingKey) savedApiKeysRef.current[settings.ai_provider] = settings.ai_api_key;
    }

    setUseCustomProvider(isCustom);
    setTestStatus('idle');

    if (isCustom) {
      setSettings(prev => ({ ...prev, ai_provider: '', ai_model: '', ai_api_key: '' }));
      setSavedKeyLength(0);
      setIsEditingKey(true);
    } else {
      const len = savedKeyLengthsRef.current[value] || 0;
      setSavedKeyLength(len);
      setIsEditingKey(false);
      setSettings(prev => ({
        ...prev,
        ai_provider: value,
        ai_model:    savedModelsRef.current[value] || DEFAULT_MODELS[value] || '',
        ai_api_key:  '',
      }));
      setUseCustomModel(false);
    }
  };

  const handleModelChange = (value: string) => {
    const isCustom = value === 'custom';
    setUseCustomModel(isCustom);
    if (!isCustom) {
      savedModelsRef.current[settings.ai_provider] = value;
      setSettings(prev => ({ ...prev, ai_model: value }));
    } else {
      setSettings(prev => ({ ...prev, ai_model: '' }));
    }
  };

  const handleInputChange = (field: keyof AISettings, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    if (field === 'ai_api_key') {
      setIsEditingKey(true);
      if (PROVIDER_MODELS[settings.ai_provider]) {
        savedApiKeysRef.current[settings.ai_provider] = value;
      }
    } else {
      setTestStatus('idle');
    }
  };

  const selectedModelInfo = PROVIDER_MODELS[settings.ai_provider]?.find(m => m.id === settings.ai_model);

  const contextColor = (k: number) => {
    if (k >= 128) return 'text-green-600 bg-green-50';
    if (k >= 32)  return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const TABS = [
    { id: 'platform'     as SettingsSection, label: 'Platform',      icon: Settings },
    { id: 'ai'           as SettingsSection, label: 'AI Assistant',  icon: Bot    },
    { id: 'integrations' as SettingsSection, label: 'Integrations',  icon: Puzzle },
    { id: 'email'        as SettingsSection, label: 'Email / SMTP',  icon: Mail   },
  ];

  // Platform settings state
  const [platformConfigs, setPlatformConfigs] = useState<any[]>([]);
  const [loadingPlatform, setLoadingPlatform] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState(false);

  const loadPlatformSettings = async () => {
    setLoadingPlatform(true);
    try {
      const res = await fetch('/api/settings/system', {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const result = await res.json();
        setPlatformConfigs((result.data as any[]).filter(cfg => cfg.category === 'system'));
      }
    } catch (err) {
      console.error('Failed to load platform settings', err);
    } finally {
      setLoadingPlatform(false);
    }
  };

  useEffect(() => {
    loadPlatformSettings();
  }, []);

  const handlePlatformToggle = async (key: string, newValue: string, configType?: string) => {
    setSavingPlatform(true);
    try {
      const csrf = getCsrfToken();
      const res = await fetch(`/api/settings/system/${key}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        body: JSON.stringify({ value: newValue, type: configType || 'string' })
      });
      if (res.ok) {
        setPlatformConfigs(prev => prev.map(c => c.config_key === key ? { ...c, config_value: newValue } : c));
      }
    } catch (err) {
      console.error('Failed to update platform setting', err);
    } finally {
      setSavingPlatform(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">

        {/* ── Page header ───────────────────────────────────────── */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 text-left">System Settings</h1>
          <p className="text-sm text-slate-500 mt-1 text-left">
            Manage platform configuration, AI, integrations, and email settings
          </p>
        </div>

        {/* ── Tab navigation ────────────────────────────────────── */}
        <div className="flex gap-2">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Section content ───────────────────────────────────── */}
        <div>
          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <RefreshCw className="text-blue-600 animate-spin" size={40} />
            </div>
          ) : activeSection === 'platform' ? (

            /* ════════════ PLATFORM SETTINGS SECTION ════════════ */
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Settings size={18} className="text-blue-600" /> Platform Settings
                </h2>
                <p className="text-sm text-slate-500 mt-0.5 text-left">System-wide feature toggles and configuration</p>
              </div>

              {loadingPlatform ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="text-blue-600 animate-spin" size={24} />
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-100">
                  {platformConfigs.map(cfg => (
                    <div key={cfg.config_key} className="flex items-center justify-between px-6 py-4">
                      <div className="text-left">
                        <p className="text-sm font-semibold text-slate-800 text-left">
                          {cfg.config_key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 text-left">{cfg.description || cfg.config_key}</p>
                      </div>
                      {cfg.config_type === 'boolean' ? (
                        <button
                          onClick={() => handlePlatformToggle(cfg.config_key, cfg.config_value === 'true' || cfg.config_value === '1' ? 'false' : 'true', 'boolean')}
                          disabled={savingPlatform}
                          className={`relative w-11 h-6 rounded-full transition-colors ${
                            cfg.config_value === 'true' || cfg.config_value === '1' ? 'bg-emerald-500' : 'bg-slate-300'
                          }`}
                        >
                          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            cfg.config_value === 'true' || cfg.config_value === '1' ? 'left-[22px]' : 'left-0.5'
                          }`} />
                        </button>
                      ) : (
                        <input
                          type="text"
                          value={cfg.config_value || ''}
                          onChange={(e) => setPlatformConfigs(prev => prev.map(c => c.config_key === cfg.config_key ? { ...c, config_value: e.target.value } : c))}
                          onBlur={(e) => handlePlatformToggle(cfg.config_key, e.target.value, cfg.config_type || 'string')}
                          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                        />
                      )}
                    </div>
                  ))}
                  {platformConfigs.length === 0 && (
                    <div className="px-6 py-8 text-center text-sm text-slate-500">
                      No platform settings found.
                    </div>
                  )}
                </div>
              )}
            </div>

          ) : activeSection === 'ai' ? (

            /* ════════════ AI ASSISTANT SECTION ════════════ */
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Bot size={18} className="text-blue-600" /> AI Assistant
                </h2>
                <p className="text-sm text-slate-500 mt-0.5 text-left">Configure the AI model powering the assistant, alerts, and smart suggestions for all users.</p>
              </div>

              {/* Provider + Model in one card */}
              <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-100">

                {/* Provider row */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings size={14} className="text-slate-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Provider</span>
                  </div>
                  <select
                    value={useCustomProvider ? 'custom' : settings.ai_provider}
                    onChange={e => handleProviderChange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(PROVIDER_LABELS).map(([id, label]) => (
                      <option key={id} value={id}>{label}</option>
                    ))}
                    <option value="custom">Custom Provider</option>
                  </select>
                  {useCustomProvider && (
                    <input
                      type="text"
                      value={settings.ai_provider}
                      onChange={e => handleInputChange('ai_provider', e.target.value)}
                      placeholder="Provider name (e.g. cohere, mistral)"
                      className="mt-2 w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                {/* Model row */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Cpu size={14} className="text-slate-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Model</span>
                  </div>
                  {!useCustomModel && !useCustomProvider ? (
                    <select
                      value={settings.ai_model}
                      onChange={e => handleModelChange(e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {(PROVIDER_MODELS[settings.ai_provider] || []).map(m => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                      <option value="custom">Custom model...</option>
                    </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        value={settings.ai_model}
                        onChange={e => handleInputChange('ai_model', e.target.value)}
                        placeholder="Model name (e.g. gpt-4o, claude-3-opus-20240229)"
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {useCustomModel && !useCustomProvider && (
                        <button onClick={() => { setUseCustomModel(false); setSettings(prev => ({ ...prev, ai_model: DEFAULT_MODELS[settings.ai_provider] || '' })); }} className="mt-1 text-xs text-blue-600 hover:underline">Back to predefined models</button>
                      )}
                    </div>
                  )}
                  {selectedModelInfo && (
                    <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${contextColor(selectedModelInfo.contextK)}`}>
                      <Info size={11} />
                      {selectedModelInfo.contextK >= 1000 ? `${(selectedModelInfo.contextK / 1000).toFixed(0)}M` : `${selectedModelInfo.contextK}k`} token context
                    </div>
                  )}
                  {settings.ai_model === 'gpt-4' && (
                    <div className="mt-3 flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                      <AlertCircle size={13} className="mt-0.5 shrink-0" />
                      <span><strong>Warning:</strong> GPT-4 has an 8k limit. Use GPT-4o or GPT-4.1 instead.</span>
                    </div>
                  )}
                </div>

                {/* API key row */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Key size={14} className="text-slate-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {settings.ai_provider ? `${PROVIDER_LABELS[settings.ai_provider] || settings.ai_provider} API Key` : 'API Key'}
                    </span>
                  </div>
                  {/* Bullet placeholder when a key is saved but not being edited */}
                  {!isEditingKey && savedKeyLength > 0 && (
                    <div
                      className="w-full px-3 py-2.5 border border-slate-200 bg-slate-50 rounded-lg cursor-pointer flex items-center justify-between gap-3 hover:border-blue-300 transition-colors"
                      onClick={() => { setIsEditingKey(true); setSettings(prev => ({ ...prev, ai_api_key: '' })); }}
                      title="Click to replace the API key"
                    >
                      <span className="font-mono text-sm text-slate-500 tracking-widest overflow-hidden whitespace-nowrap flex-1" style={{ letterSpacing: '0.15em' }}>
                        {'•'.repeat(savedKeyLength)}
                      </span>
                      <span className="text-xs font-sans text-slate-400 whitespace-nowrap shrink-0 bg-slate-100 px-2 py-0.5 rounded-full">
                        click to replace
                      </span>
                    </div>
                  )}

                  {/* Input shown when editing or no key saved yet */}
                  {(isEditingKey || savedKeyLength === 0) && (
                    <div className="space-y-1">
                      <div className="relative">
                        <input
                          type={showAiKey ? 'text' : 'password'}
                          value={settings.ai_api_key}
                          onChange={e => handleInputChange('ai_api_key', e.target.value)}
                          placeholder="Paste your API key here"
                          autoComplete="off"
                          autoFocus={isEditingKey}
                          className="w-full px-3 py-2.5 pr-10 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowAiKey(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          title={showAiKey ? 'Hide key' : 'Show key'}
                        >
                          {showAiKey ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      {isEditingKey && savedKeyLength > 0 && (
                        <button
                          type="button"
                          onClick={() => { setIsEditingKey(false); setSettings(prev => ({ ...prev, ai_api_key: '' })); }}
                          className="text-xs text-slate-500 hover:text-slate-700 underline"
                        >
                          Cancel -- keep existing key
                        </button>
                      )}
                    </div>
                  )}
                  <p className="mt-1.5 text-xs text-slate-400">Stored encrypted. Exact length shown. Never transmitted in plain text.</p>

                  {testStatus === 'success' && <div className="mt-3 flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-2.5 text-sm"><CheckCircle size={14} /><span>{testMessage}</span></div>}
                  {testStatus === 'error'   && <div className="mt-3 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 text-sm"><AlertCircle size={14} /><span>{testMessage}</span></div>}
                  {saveStatus === 'success' && <div className="mt-3 flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-2.5 text-sm"><CheckCircle size={14} /><span>Settings saved. Cache cleared -- takes effect immediately.</span></div>}
                  {saveStatus === 'error'   && <div className="mt-3 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 text-sm"><AlertCircle size={14} /><span>{errorMessage}</span></div>}

                  <div className="mt-4 flex items-center gap-3 pt-4 border-t border-slate-100">
                    <button onClick={handleSave} disabled={isSaving || isTesting} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
                      {isSaving ? <RefreshCw className="animate-spin" size={14} /> : <Save size={14} />}
                      {isSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                    <button onClick={handleTestConnection} disabled={isSaving || isTesting || (!settings.ai_api_key && savedKeyLength === 0)} className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 text-sm font-medium">
                      {isTesting ? <RefreshCw className="animate-spin" size={14} /> : <Zap size={14} />}
                      {isTesting ? 'Testing...' : 'Test Connection'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick reference */}
              <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Provider Quick Reference</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
                  <div className="flex gap-1.5"><span className="font-semibold text-slate-700">OpenAI:</span> GPT-4o / GPT-4.1 -- 128k-1M ctx</div>
                  <div className="flex gap-1.5"><span className="font-semibold text-slate-700">Claude:</span> Sonnet 4.6 -- 200k ctx -- best reasoning</div>
                  <div className="flex gap-1.5"><span className="font-semibold text-slate-700">Gemini:</span> 2.0 Flash -- 1M ctx -- vision-capable</div>
                  <div className="flex gap-1.5"><span className="font-semibold text-slate-700">Z.AI:</span> GLM-4 Plus -- 128k -- cost-effective</div>
                  <div className="flex gap-1.5"><span className="font-semibold text-slate-700">xAI Grok:</span> Grok 4 -- 128k -- real-time knowledge</div>
                </div>
              </div>
            </div>

          ) : activeSection === 'integrations' ? (

            /* ════════════ INTEGRATIONS SECTION ════════════ */
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Puzzle size={18} className="text-blue-600" /> Integrations
                </h2>
                <p className="text-sm text-slate-500 mt-0.5 text-left">Connect third-party services to extend platform functionality.</p>
              </div>

              {/* Companies House card */}
              <div className="bg-white rounded-3xl border border-slate-100">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <Building2 size={18} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Companies House</p>
                      <p className="text-xs text-slate-400">UK company registry -- auto-fill company profiles</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                    chStatus?.configured
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${chStatus?.configured ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                    {chStatus?.configured ? 'Connected' : 'Not configured'}
                  </span>
                </div>

                <div className="p-5 space-y-4">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    When configured, system admins can search the UK Companies House registry when creating a company — name, address, registration number and industry are filled in automatically.
                    Get your free API key at <span className="text-blue-600 font-medium">developer.company-information.service.gov.uk</span>
                  </p>

                  {chStatus?.configured && (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-xs text-emerald-700">
                      <Key size={11} className="text-emerald-500" />
                      <span className="font-mono">{chStatus.maskedKey}</span>
                      <span className="text-emerald-400 ml-auto">via {chStatus.source}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      {chStatus?.configured ? 'Replace API Key' : 'API Key'}
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={chShowKey ? 'text' : 'password'}
                          value={chKey}
                          onChange={e => setChKey(e.target.value)}
                          placeholder={chStatus?.configured ? 'Enter new key to replace existing' : 'Paste your Companies House API key'}
                          autoComplete="off"
                          className="w-full px-3 py-2.5 pr-9 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="button" onClick={() => setChShowKey(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          {chShowKey ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                      <button onClick={handleChSave} disabled={chSaving || !chKey.trim()} className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium shrink-0">
                        {chSaving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                        {chSaving ? 'Saving…' : 'Save Key'}
                      </button>
                      {chStatus?.configured && chStatus.source === 'database' && (
                        <button onClick={handleChRemove} disabled={chRemoving} title="Remove key" className="flex items-center gap-1.5 px-3 py-2.5 text-red-500 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 shrink-0">
                          {chRemoving ? <RefreshCw size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        </button>
                      )}
                    </div>
                  </div>

                  {chMsg && (
                    <div className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${chMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                      {chMsg.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                      {chMsg.text}
                    </div>
                  )}
                </div>
              </div>
            </div>

          ) : activeSection === 'email' ? (

            /* ════════════ EMAIL / SMTP SECTION ════════════ */
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Mail size={18} className="text-blue-600" /> Email / SMTP
                </h2>
                <p className="text-sm text-slate-500 mt-0.5 text-left">Configure the outgoing mail server used for password resets, notifications, and system emails.</p>
              </div>

              <div className="bg-white rounded-3xl border border-slate-100">
                {loadingEmail ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="text-blue-600 animate-spin" size={40} />
                  </div>
                ) : (
                  <div className="p-5 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">SMTP Host</label>
                        <input
                          type="text"
                          value={emailSettings.email_host}
                          onChange={e => setEmailSettings(prev => ({ ...prev, email_host: e.target.value }))}
                          placeholder="e.g. mail.privateemail.com"
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">SMTP Port</label>
                        <input
                          type="number"
                          value={emailSettings.email_port}
                          onChange={e => setEmailSettings(prev => ({ ...prev, email_port: parseInt(e.target.value) || 587 }))}
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Username</label>
                        <input
                          type="text"
                          value={emailSettings.email_user}
                          onChange={e => setEmailSettings(prev => ({ ...prev, email_user: e.target.value }))}
                          placeholder="e.g. noreply@yourdomain.com"
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Password</label>
                        <input
                          type="password"
                          value={emailSettings.email_password}
                          onChange={e => setEmailSettings(prev => ({ ...prev, email_password: e.target.value }))}
                          autoComplete="new-password"
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Default From Email</label>
                        <input
                          type="email"
                          value={emailSettings.default_from_email}
                          onChange={e => setEmailSettings(prev => ({ ...prev, default_from_email: e.target.value }))}
                          placeholder="e.g. noreply@yourdomain.com"
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Secure Connection (SSL/TLS)</label>
                        <select
                          value={emailSettings.email_secure.toString()}
                          onChange={e => setEmailSettings(prev => ({ ...prev, email_secure: e.target.value === 'true' }))}
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="true">Yes (SSL/TLS — port 465)</option>
                          <option value="false">No (STARTTLS — port 587)</option>
                        </select>
                      </div>
                    </div>

                    {emailSaveMsg && (
                      <div className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${emailSaveMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                        {emailSaveMsg.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                        {emailSaveMsg.text}
                      </div>
                    )}

                    <div className="pt-3 border-t border-slate-100">
                      <button
                        onClick={saveEmailSettings}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        <Save size={14} /> Save SMTP Settings
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
